/** Measure catalogue modal — all measures with photos and descriptions.

Self-contained: manages its own open/close state and fetches catalogue
data via useCatalogue. Shows all measures from config (not filtered by
municipality), grouped by species or standalone status. Each entry displays the photo
alongside the description and details — no dropdown, everything visible
at once. No colour legend.

Mounted independently on both the landing page and the in-app header —
each instance owns its own state, same pattern as AboutPanel.
 */

import { useEffect, useId, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { GanttChart } from "./GanttChart";
import { useCatalogue } from "../hooks/useCatalogue";
import { useLanguageContext } from "../hooks/useLanguage";
import { renderEmphasis } from "../utils/emphasis";
import type { CatalogueEntry, RegionConfig } from "../types";

interface MeasureCatalogueModalProps {
  config: RegionConfig;
}

type CatalogueView = "list" | "gantt";
const STANDALONE_GROUP_ID = "__standalone__";

export function MeasureCatalogueModal({ config }: MeasureCatalogueModalProps) {
  const { t, lang } = useLanguageContext();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<CatalogueView>("list");
  const { catalogue, loading, error } = useCatalogue(true);
  const panelId = useId();
  const [imgErrors, setImgErrors] = useState<Set<string>>(new Set());

  const handleImgError = (measureId: string) => {
    setImgErrors((prev) => new Set(prev).add(measureId));
  };

  /** Get the localised label for a measure, falling back to the config label. */
  const measureLabel = (measureId: string, fallback: string): string => {
    const key = `measure_${measureId}`;
    const val = t(key);
    return val === key ? fallback : val;
  };

  /** Get the localised label_with_dates for a measure. */
  const measureLabelDates = (measureId: string, fallback: string): string => {
    const key = `measure_${measureId}_dates`;
    const val = t(key);
    return val === key ? fallback : val;
  };

  /** Get the localised species name. */
  const speciesName = (speciesId: string, fallback: string): string => {
    const key = `species_${speciesId}`;
    const val = t(key);
    return val === key ? fallback : val;
  };

  /** Pick the localised text field based on current language. */
  const localisedText = (de: string, en: string): string => (lang === "en" ? en || de : de);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handleEscape);
    // Lock body scroll while modal is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  // Build a lookup: measure_id → catalogue entry
  const catalogueById = useMemo(
    () => new Map(catalogue.map((entry) => [entry.measure_id, entry])),
    [catalogue],
  );

  // Group measures by species, using config as the source of truth for order.
  // Species-less measures are standalone, so they must not be grouped under
  // the species whose raw GeoJSON happened to carry their flag column.
  const measuresBySpecies = useMemo(() => {
    const groups = new Map<string, typeof config.measures>();
    for (const measure of config.measures) {
      const groupId = measure.species || STANDALONE_GROUP_ID;
      const list = groups.get(groupId);
      if (list) list.push(measure);
      else groups.set(groupId, [measure]);
    }
    return groups;
  }, [config.measures]);

  const speciesNameById = useMemo(
    () => new Map(config.species.map((s) => [s.id, s.name])),
    [config.species],
  );
  const catalogueGroupName = (speciesId: string): string =>
    speciesId === STANDALONE_GROUP_ID
      ? t("standalone_measures")
      : speciesName(speciesId, speciesNameById.get(speciesId) || speciesId);

  const modalContent = open ? (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={panelId}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-0 sm:p-4"
    >
      {/* Scrim */}
      <button
        type="button"
        onClick={() => setOpen(false)}
        aria-label={t("close")}
        className="absolute inset-0 bg-flima-spruce/40 backdrop-blur-sm"
        tabIndex={-1}
      />

      {/* Modal panel — wide */}
      <div
        id={panelId}
        className="flima-screen relative flex w-screen flex-col overflow-hidden border border-flima-spruce/10 bg-white shadow-panel sm:max-h-[88vh] sm:w-full sm:max-w-5xl sm:rounded-2xl"
      >
        {/* Header */}
        <div className="flex shrink-0 flex-col gap-3 border-b border-flima-spruce/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-8 sm:py-4">
          <div className="min-w-0 pr-12 sm:pr-0">
            <h2 className="font-display text-xl font-semibold text-flima-spruce">
              {t("catalogue")}
            </h2>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {/* View toggle */}
            <div className="grid flex-1 grid-cols-2 rounded-md border border-flima-spruce/15 bg-flima-mist/40 p-0.5 sm:flex sm:flex-none">
              <button
                type="button"
                onClick={() => setView("list")}
                aria-pressed={view === "list"}
                className={`min-h-10 rounded px-2.5 py-1 font-utility text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river motion-reduce:transition-none ${
                  view === "list"
                    ? "bg-white text-flima-spruce shadow-sm"
                    : "text-flima-slate/60 hover:text-flima-spruce"
                }`}
              >
                {t("view_list")}
              </button>
              <button
                type="button"
                onClick={() => setView("gantt")}
                aria-pressed={view === "gantt"}
                className={`min-h-10 rounded px-2.5 py-1 font-utility text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river motion-reduce:transition-none ${
                  view === "gantt"
                    ? "bg-white text-flima-spruce shadow-sm"
                    : "text-flima-slate/60 hover:text-flima-spruce"
                }`}
              >
                {t("view_gantt")}
              </button>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute right-3 top-3 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-2xl leading-none text-flima-slate/55 transition-colors hover:bg-flima-mist hover:text-flima-spruce focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river motion-reduce:transition-none sm:static"
              aria-label={t("close")}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
        </div>

        {/* Body — scrollable */}
        <div className="flima-safe-pb min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-8 sm:py-5">
          {loading ? (
            <div className="flex items-center justify-center py-12" role="status">
              <span className="text-sm text-flima-slate/60">{t("loading")}</span>
            </div>
          ) : error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4" role="alert">
              <p className="text-sm font-semibold text-red-700">
                {t("error")}: {error}
              </p>
            </div>
          ) : view === "gantt" ? (
            <GanttChart config={config} catalogue={catalogue} />
          ) : (
            <div className="space-y-8">
              {Array.from(measuresBySpecies.entries()).map(([speciesId, measures]) => (
                <section key={speciesId}>
                  <h3 className="mb-4 font-display text-base font-semibold text-flima-spruce">
                    {catalogueGroupName(speciesId)}
                  </h3>
                  <div className="space-y-4">
                    {measures.map((measure) => {
                      const entry: CatalogueEntry | undefined =
                        catalogueById.get(measure.id);
                      const hasImage = Boolean(entry?.image_path) && !imgErrors.has(measure.id);
                      const label = measureLabel(measure.id, measure.label);
                      const labelDates = measureLabelDates(measure.id, measure.label_with_dates);
                      const description = entry
                        ? localisedText(entry.description, entry.description_en)
                        : "";
                      const details = entry
                        ? localisedText(entry.details, entry.details_en)
                        : "";
                      return (
                        <article
                          key={measure.id}
                          className="overflow-hidden rounded-lg border border-flima-spruce/10 bg-flima-mist/30"
                        >
                          <div className="flex flex-col sm:flex-row">
                            {hasImage ? (
                              <div className="shrink-0 sm:w-56">
                                <img
                                  src={entry!.image_path}
                                  alt={label}
                                  loading="lazy"
                                  onError={() => handleImgError(measure.id)}
                                  className="h-48 w-full bg-flima-mist/40 object-contain sm:h-full"
                                />
                              </div>
                            ) : null}
                            <div className="min-w-0 flex-1 p-4 sm:p-5">
                              <p className="font-utility text-[0.62rem] uppercase tracking-[0.14em] text-flima-river">
                                {labelDates}
                              </p>
                              <h4 className="mt-1 break-words font-display text-base font-semibold text-flima-spruce">
                                {label}
                              </h4>
                              {description ? (
                                <p className="mt-2 break-words text-sm leading-relaxed text-flima-slate/80">
                                  {renderEmphasis(description)}
                                </p>
                              ) : null}
                              {details ? (
                                <p className="mt-3 break-words text-xs leading-relaxed text-flima-slate/65">
                                  {renderEmphasis(details)}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        data-tour-id="catalogue-button"
        onClick={() => setOpen(true)}
        aria-label={t("catalogue")}
        className="flex min-h-11 items-center gap-2 rounded-md px-3 text-sm font-semibold text-white/80 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-flima-spruce motion-reduce:transition-none"
      >
        <svg
          viewBox="0 0 20 20"
          className="h-5 w-5 sm:hidden"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          aria-hidden="true"
        >
          <path d="M4 5h12M4 10h12M4 15h8" strokeLinecap="round" />
        </svg>
        <span className="hidden sm:inline">{t("catalogue")}</span>
      </button>

      {modalContent ? createPortal(modalContent, document.body) : null}
    </div>
  );
}
